// Types for the What Fits My Lot Wizard

export interface PropertyData {
  address: string;
  city?: string;
  county?: string;
  zipCode?: string;
  latitude: number;
  longitude: number;
}

export interface WizardFormData {
  // Step 1: Address (from PropertyData)
  propertyData: PropertyData | null;
  
  // Step 2: Homeowner Info
  firstName: string;
  lastName: string;
  projectAddress: string;
  phone: string;
  
  // Step 3: ADU Type
  aduType: string;
  
  // Step 4: Purpose
  purposes: string[];
  otherPurpose: string;
  
  // Step 5: Design & Permitting Status
  designChoice: string;
  
  // Step 6: Size & Materials (conditional)
  squareFootage: string;
  finishLevel: string;
  
  // Step 7: Contact & Verification
  email: string;
  password: string;
  confirmPassword: string;
  smsConsent: boolean;
  verificationCode: string;
  
  // Account creation
  isVerified: boolean;
  accountCreated: boolean;
}

export interface WizardStep {
  id: number;
  title: string;
  description: string;
}

export const WIZARD_STEPS: WizardStep[] = [
  { id: 1, title: "Property Address", description: "Enter your property address" },
  { id: 2, title: "Your Information", description: "Tell us about yourself" },
  { id: 3, title: "ADU Type", description: "What type of ADU are you considering?" },
  { id: 4, title: "Purpose", description: "What's the purpose of your ADU?" },
  { id: 5, title: "Design Status", description: "Where are you in the design process?" },
  { id: 6, title: "Size & Materials", description: "Customize your ADU" },
  { id: 7, title: "Verify & Create Account", description: "Secure your results" },
  { id: 8, title: "Your Results", description: "View your ADU potential" },
];

export const ADU_TYPES = [
  { value: "detached", label: "Detached ADU", description: "A standalone structure separate from your main home" },
  { value: "attached", label: "Attached ADU", description: "Connected to your existing home" },
  { value: "jadu", label: "Junior ADU (JADU)", description: "A smaller unit within your existing home (up to 500 sq ft)" },
  { value: "garage", label: "Garage Conversion", description: "Convert your existing garage into living space" },
  { value: "basement", label: "Basement Conversion", description: "Convert your basement into a living unit" },
  { value: "not_sure", label: "I'm Not Sure", description: "We'll help you determine the best option" },
];

export const PURPOSE_OPTIONS = [
  { value: "rental", label: "Additional income / rental property" },
  { value: "elderly_parents", label: "Housing for elderly parents" },
  { value: "young_adults", label: "Housing for young adults" },
  { value: "guest_house", label: "Guest house" },
  { value: "home_office", label: "Home office / studio" },
  { value: "future_downsizing", label: "Future downsizing / personal use" },
  { value: "other", label: "Other" },
];

export const DESIGN_OPTIONS = [
  { value: "have_permit", label: "I already have approved plans/permits" },
  { value: "working_with_architect", label: "I'm working with an architect or designer" },
  { value: "want_custom", label: "I'd like a fully custom ADU from UDA" },
  { value: "pre_designed", label: "I'd prefer to choose from UDA's pre-designed plans" },
  { value: "not_sure", label: "Not sure yet" },
];

export const FINISH_LEVELS = [
  { value: "standard", label: "Standard", cost: 99, description: "Quality finishes at an affordable price" },
  { value: "elite", label: "Elite", cost: 198, description: "Premium materials and upgraded fixtures" },
  { value: "deluxe", label: "Deluxe", cost: 297, description: "Luxury finishes throughout" },
];

export const initialWizardFormData: WizardFormData = {
  propertyData: null,
  firstName: "",
  lastName: "",
  projectAddress: "",
  phone: "",
  aduType: "",
  purposes: [],
  otherPurpose: "",
  designChoice: "",
  squareFootage: "",
  finishLevel: "",
  email: "",
  password: "",
  confirmPassword: "",
  smsConsent: false,
  verificationCode: "",
  isVerified: false,
  accountCreated: false,
};
